import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import {
  isContextMaterialIndexingFailed,
  isContextMaterialIndexingInProgress,
  isContextMaterialIndexingReady,
} from '@digital-twins/shared-types'
import i18n from '@/i18n'
import { Badge } from '@/components/ui/badge'

interface KnowledgeIndexingBadgeProps {
  status: string | null | undefined
}

/** Map real indexing statuses to Library ingest stage copy (PRD). */
export function indexingStageLabel(status: string | null | undefined): string {
  if (isContextMaterialIndexingReady(status)) {
    return i18n.t('library.stage.ready', { ns: 'app' })
  }
  if (isContextMaterialIndexingFailed(status)) {
    return i18n.t('library.stage.failed', { ns: 'app' })
  }
  if (status === 'uploading' || status === 'pending') {
    return i18n.t('library.stage.queued', { ns: 'app' })
  }
  if (status === 'understanding') {
    return i18n.t('library.stage.preparingAnswers', { ns: 'app' })
  }
  if (status === 'extracting') {
    return i18n.t('library.stage.reading', { ns: 'app' })
  }
  if (isContextMaterialIndexingInProgress(status)) {
    return i18n.t('library.stage.reading', { ns: 'app' })
  }
  return status || i18n.t('library.stage.unknown', { ns: 'app' })
}

export function KnowledgeIndexingBadge({ status }: KnowledgeIndexingBadgeProps) {
  const label = indexingStageLabel(status)
  if (isContextMaterialIndexingReady(status)) {
    return (
      <Badge variant="positive" className="shrink-0 gap-1" data-testid="knowledge-status-ready">
        <CheckCircle2 className="h-3 w-3" />
        {label}
      </Badge>
    )
  }
  if (isContextMaterialIndexingFailed(status)) {
    return (
      <Badge variant="destructive" className="shrink-0 gap-1" data-testid="knowledge-status-failed">
        <XCircle className="h-3 w-3" />
        {label}
      </Badge>
    )
  }
  if (isContextMaterialIndexingInProgress(status)) {
    return (
      <Badge variant="muted" className="shrink-0 gap-1" data-testid="knowledge-status-pending">
        <Loader2 className="h-3 w-3 animate-spin" />
        {label}
      </Badge>
    )
  }
  return (
    <Badge variant="muted" className="shrink-0" data-testid="knowledge-status-unknown">
      {label}
    </Badge>
  )
}
